import type { RuleBasedErrorEntry } from '@/lib/api/proofreading';
import type { ProofreadingIssueStatus } from '@/types/proofreading';
import {
  createProofreadingTextCache,
  lineWordToOffset,
  offsetToLineWord,
  snippetMatchesAtLineWord,
  type ProofreadingTextCache,
} from '@/lib/utils/proofreadingPositions';
import type {
  RuleBasedErrorEntryStored,
  VariationSummaryItemStored,
  VariationSummaryPositionStored,
} from '@/lib/utils/proofreadingStoredTypes';
import { buildIssueId, buildVariationLocationId } from '@/lib/utils/proofreadingTransform';

function isAppliedStatus(
  issueId: string,
  issueStatuses?: Map<string, ProofreadingIssueStatus>,
): boolean {
  return issueStatuses?.get(issueId) === 'applied';
}

export function validateRuleBasedEntryCoordinates(
  text: string,
  entry: RuleBasedErrorEntryStored,
  issueStatuses?: Map<string, ProofreadingIssueStatus>,
  textCache?: ProofreadingTextCache,
): RuleBasedErrorEntryStored {
  if (entry.coord_invalid) {
    return entry;
  }

  const issueId = buildIssueId(entry);
  if (isAppliedStatus(issueId, issueStatuses)) {
    return entry;
  }

  const cache = textCache ?? createProofreadingTextCache(text);
  const start = lineWordToOffset(text, entry.start_line, entry.start_word, cache);
  if (start === null) {
    return { ...entry, coord_invalid: true };
  }

  if (!snippetMatchesAtLineWord(text, entry.start_line, entry.start_word, entry.target_text, cache)) {
    return { ...entry, coord_invalid: true };
  }

  const roundTrip = offsetToLineWord(text, start, cache);
  if (roundTrip.line !== entry.start_line || roundTrip.word !== entry.start_word) {
    return { ...entry, coord_invalid: true };
  }

  return entry;
}

export function validateVariationPositionCoordinates(
  text: string,
  item: VariationSummaryItemStored,
  pos: VariationSummaryPositionStored,
  variantText: string,
  issueStatuses?: Map<string, ProofreadingIssueStatus>,
  textCache?: ProofreadingTextCache,
): VariationSummaryPositionStored {
  if (pos.coord_invalid) {
    return pos;
  }

  const issueId = buildVariationLocationId(item, pos, variantText);
  if (isAppliedStatus(issueId, issueStatuses)) {
    return pos;
  }

  const cache = textCache ?? createProofreadingTextCache(text);
  const start = lineWordToOffset(text, pos.start_line, pos.start_word, cache);
  if (start === null) {
    return { ...pos, coord_invalid: true };
  }

  if (!snippetMatchesAtLineWord(text, pos.start_line, pos.start_word, variantText, cache)) {
    return { ...pos, coord_invalid: true };
  }

  const roundTrip = offsetToLineWord(text, start, cache);
  if (roundTrip.line !== pos.start_line || roundTrip.word !== pos.start_word) {
    return { ...pos, coord_invalid: true };
  }

  return pos;
}

export function validateSyncedCoordinates(params: {
  text: string;
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItemStored[];
  issueStatuses?: Map<string, ProofreadingIssueStatus>;
}): {
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItemStored[];
} {
  const { text, results, variationSummary, issueStatuses } = params;
  const textCache = createProofreadingTextCache(text);

  const validatedResults = results.map((entry) =>
    validateRuleBasedEntryCoordinates(text, entry, issueStatuses, textCache),
  );

  const validatedVariation = variationSummary.map((item) => ({
    ...item,
    variants: item.variants.map((variant) => ({
      ...variant,
      position: (variant.position ?? []).map((pos) =>
        validateVariationPositionCoordinates(
          text,
          item,
          pos,
          variant.text,
          issueStatuses,
          textCache,
        ),
      ),
    })),
  }));

  return {
    results: validatedResults,
    variationSummary: validatedVariation,
  };
}

/** 単一原稿のシンク時用。他原稿の座標は検証せずそのまま残す。 */
export function validateSyncedCoordinatesForManuscript(params: {
  manuscriptId: string;
  text: string;
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItemStored[];
  issueStatuses?: Map<string, ProofreadingIssueStatus>;
}): {
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItemStored[];
} {
  const { manuscriptId, text, results, variationSummary, issueStatuses } = params;
  const textCache = createProofreadingTextCache(text);

  const validatedResults = results.map((entry) => {
    if (entry.manuscript_id !== manuscriptId) {
      return entry;
    }
    return validateRuleBasedEntryCoordinates(text, entry, issueStatuses, textCache);
  });

  const validatedVariation = variationSummary.map((item) => ({
    ...item,
    variants: item.variants.map((variant) => ({
      ...variant,
      position: (variant.position ?? []).map((pos) => {
        if (pos.manuscript_id !== manuscriptId) {
          return pos;
        }
        return validateVariationPositionCoordinates(
          text,
          item,
          pos,
          variant.text,
          issueStatuses,
          textCache,
        );
      }),
    })),
  }));

  return {
    results: validatedResults,
    variationSummary: validatedVariation,
  };
}

export function validateWorkProgressForManuscripts(params: {
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItemStored[];
  textByManuscriptId: Map<string, string>;
  issueStatuses?: Map<string, ProofreadingIssueStatus>;
}): {
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItemStored[];
} {
  const { results, variationSummary, textByManuscriptId, issueStatuses } = params;
  const textCacheByManuscriptId = new Map<string, ProofreadingTextCache>();

  const getTextCache = (manuscriptKey: string, text: string): ProofreadingTextCache => {
    const cached = textCacheByManuscriptId.get(manuscriptKey);
    if (cached) {
      return cached;
    }
    const next = createProofreadingTextCache(text);
    textCacheByManuscriptId.set(manuscriptKey, next);
    return next;
  };

  const validatedResults = results.map((entry) => {
    const text = textByManuscriptId.get(entry.manuscript_id);
    if (text === undefined) {
      return entry;
    }
    return validateRuleBasedEntryCoordinates(
      text,
      entry,
      issueStatuses,
      getTextCache(entry.manuscript_id, text),
    );
  });

  const validatedVariation = variationSummary.map((item) => ({
    ...item,
    variants: item.variants.map((variant) => ({
      ...variant,
      position: (variant.position ?? []).map((pos) => {
        const text = textByManuscriptId.get(pos.manuscript_id);
        if (text === undefined) {
          return pos;
        }
        return validateVariationPositionCoordinates(
          text,
          item,
          pos,
          variant.text,
          issueStatuses,
          getTextCache(pos.manuscript_id, text),
        );
      }),
    })),
  }));

  return {
    results: validatedResults,
    variationSummary: validatedVariation,
  };
}

export function ruleBasedEntryUtf16End(
  text: string,
  entry: Pick<RuleBasedErrorEntry, 'start_line' | 'start_word' | 'target_text' | 'length'>,
  entryStart: number,
): number {
  if (entry.target_text.length > 0) {
    return entryStart + entry.target_text.length;
  }
  return entryStart + entry.length;
}

export function variationPositionUtf16End(
  variantText: string,
  entryStart: number,
): number {
  return entryStart + variantText.length;
}
